/**
 * District Admin Dashboard - Multi-School Management
 *
 * Purpose: Manage multiple schools within a district
 * Routes: /admin/district/*
 *
 * Features:
 * - District-wide overview (aggregate metrics)
 * - School selector (click to view individual school)
 * - Multi-school license management
 *
 * Architecture (2025-11-14 - UUID-Based Security):
 * - District admin can view all schools in their district
 * - Clicking a school navigates to /admin/schools/{uuid}/overview
 * - Uses non-enumerable UUIDs instead of integer IDs for security
 * - Prevents enumeration attacks and information disclosure
 * - Reuses SchoolAdminDashboard for individual school views
 *
 * References:
 * - /docs/ADMIN_DASHBOARDS_IMPLEMENTATION.md
 * - /docs/ADMIN_DASHBOARD_SCALING_ARCHITECTURE.md
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/authInterceptor';
import ExportModal from './ExportModal';
import './morpheus-interface.css';

// Official Tappy Brand Colors from /morpheus-rl-marketing/tappy_brand_colors.md
const TappyBrandColors = {
  // Core Brand Identity - Teal/Blue spectrum
  primary: {
    darkTeal: '#1A3E48',           // Deep, professional
    teal: '#297A92',                // Primary brand color
    mediumTeal: '#388899',          // Interactive elements
    brandBlue: '#76909F',           // Secondary text, icons (footer color)
    brightTeal: '#42909E',          // Accents, highlights
    aqua: '#4D99A5',                // Light interactive
    lightAqua: '#5BA3AC',           // Hover states
    skyBlue: '#6DB4CD',             // Success, positive
  },

  // Accent Colors
  accent: {
    coral: '#EA6D37',               // Important actions, CTAs
    gold: '#ECC962',                // Warnings, achievements
    brightGold: '#F8C341',          // Highlights
    tan: '#C59F65',                 // Neutral accents
  },

  // Neutrals & Backgrounds
  neutral: {
    darkSlate: '#3D4954',           // Headers, primary text
    deepPurple: '#391339',          // Dark backgrounds
    mauve: '#8D7F88',               // Muted text
    mintGray: '#ABCACA',            // Light borders
    lightGray: '#D5DEDD',           // Dividers
    paleGray: '#ECF3F2',            // Light backgrounds
    offWhite: '#F5F9FA',            // Cards, surfaces
    nearWhite: '#FCFDFE',           // Pure backgrounds
  },

  // Semantic mappings for consistency
  semantic: {
    success: '#6DB4CD',             // Sky blue for success
    warning: '#ECC962',             // Gold for warnings
    error: '#EA6D37',               // Coral for errors
    info: '#42909E',                // Bright teal for info
    primary: '#297A92',             // Primary actions
    secondary: '#76909F',           // Secondary actions
  }
};

function DistrictAdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schools, setSchools] = useState([]);
  const [districtMetrics, setDistrictMetrics] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterStatus, setFilterStatus] = useState('all');

  // Energy-based learning state
  const [teachingQuality, setTeachingQuality] = useState(null);
  const [reteachROI, setReteachROI] = useState(null);
  const [universalPatterns, setUniversalPatterns] = useState(null);
  const [energyLoading, setEnergyLoading] = useState(false);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Fetch district overview on mount
  useEffect(() => {
    fetchDistrictOverview();
    fetchEnergyData();
  }, []);

  const fetchDistrictOverview = async () => {
    try {
      setLoading(true);

      // Fetch all schools in the district using new endpoint
      const response = await axios.get('/api/admin/district/schools');

      // Set schools data
      setSchools(response.data.schools || []);

      // Set district-wide metrics
      setDistrictMetrics(response.data.summary || {
        total_schools: 0,
        total_seats: 0,
        total_students: 0,
        total_teachers: 0,
        total_learning_hours: 0,
        avg_utilization_pct: 0,
        lms_students_synced: 0,
        lms_courses_synced: 0
      });

      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching district overview:', err);
      setError(err.response?.data?.error || 'Failed to load district data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnergyData = async () => {
    try {
      setEnergyLoading(true);

      // Fetch all energy data in parallel
      const [qualityResponse, roiResponse, patternsResponse] = await Promise.all([
        axios.get('/api/admin/district/teaching-quality'),
        axios.get('/api/admin/district/reteach-roi'),
        axios.get('/api/admin/district/universal-patterns')
      ]);

      setTeachingQuality(qualityResponse.data);
      setReteachROI(roiResponse.data);
      setUniversalPatterns(patternsResponse.data);
    } catch (err) {
      console.error('Error fetching energy data:', err);
      // Don't set error state - energy features are optional
    } finally {
      setEnergyLoading(false);
    }
  };

  const handleSchoolClick = (schoolUuid) => {
    // Navigate to individual school dashboard using UUID (secure, non-enumerable)
    navigate(`/admin/schools/${schoolUuid}/overview`);
  };

  // Filter and sort schools
  const getFilteredAndSortedSchools = () => {
    let filtered = schools;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(school =>
        school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        school.district_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(school => {
        if (filterStatus === 'active') {
          return school.status === 'active';
        } else if (filterStatus === 'expiring') {
          const daysUntilExpiration = school.contract?.days_until_expiration;
          return daysUntilExpiration !== null && daysUntilExpiration <= 60;
        } else if (filterStatus === 'at-risk') {
          return school.metrics?.utilization_pct >= 90;
        }
        return true;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'utilization':
          return (b.metrics?.utilization_pct || 0) - (a.metrics?.utilization_pct || 0);
        case 'students':
          return (b.metrics?.seats_used || 0) - (a.metrics?.seats_used || 0);
        case 'expiration':
          return (a.contract?.days_until_expiration || 999) - (b.contract?.days_until_expiration || 999);
        default:
          return 0;
      }
    });

    return sorted;
  };

  const filteredSchools = getFilteredAndSortedSchools();

  // Calculate alerts
  const expiringSchools = schools.filter(s =>
    s.contract?.days_until_expiration !== null && s.contract?.days_until_expiration <= 60
  ).length;

  const atCapacitySchools = schools.filter(s =>
    s.metrics?.utilization_pct >= 90
  ).length;

  // Skeleton Loading Component (2025 Notion-style)
  const SkeletonLoader = () => (
    <div className="morpheus-interface" style={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header Skeleton */}
      <div style={{
        padding: '20px 40px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{
            width: '200px',
            height: '24px',
            backgroundColor: '#e5e7eb',
            borderRadius: '6px',
            marginBottom: '8px',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }} />
          <div style={{
            width: '150px',
            height: '14px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }} />
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ width: '100px', height: '40px', backgroundColor: '#e5e7eb', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
          <div style={{ width: '80px', height: '40px', backgroundColor: '#e5e7eb', borderRadius: '8px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
        </div>
      </div>

      {/* Content Skeleton */}
      <div style={{ padding: '40px' }}>
        {/* Metrics Grid Skeleton */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '40px'
        }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '60px',
                height: '36px',
                backgroundColor: '#e5e7eb',
                borderRadius: '6px',
                marginBottom: '8px',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }} />
              <div style={{
                width: '100px',
                height: '14px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }} />
            </div>
          ))}
        </div>

        {/* Large Content Block Skeleton */}
        {[1, 2].map(i => (
          <div key={i} style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '250px',
              height: '20px',
              backgroundColor: '#e5e7eb',
              borderRadius: '6px',
              marginBottom: '24px',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }} />
            {[1, 2, 3].map(j => (
              <div key={j} style={{
                height: '80px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '16px',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }} />
            ))}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );

  // Loading state
  if (loading) {
    return <SkeletonLoader />;
  }

  // Error state
  if (error) {
    return (
      <div className="morpheus-interface" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 24px',
            borderRadius: '20px',
            backgroundColor: TappyBrandColors.neutral.paleGray,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `3px solid ${TappyBrandColors.accent.coral}`
          }}>
            <div style={{
              fontSize: '48px',
              fontWeight: 700,
              color: TappyBrandColors.accent.coral
            }}>!</div>
          </div>
          <h2 style={{
            color: TappyBrandColors.accent.coral,
            marginBottom: '20px',
            fontSize: '24px',
            fontWeight: 600,
            letterSpacing: '-0.3px'
          }}>District Dashboard Error</h2>
          <p style={{
            fontSize: '16px',
            color: TappyBrandColors.neutral.darkSlate,
            marginBottom: '30px',
            lineHeight: '1.6'
          }}>{error}</p>
          <button
            onClick={fetchDistrictOverview}
            style={{
              padding: '12px 24px',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Retry
          </button>
          <button
            onClick={onLogout}
            style={{
              padding: '12px 24px',
              marginLeft: '10px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="morpheus-interface">
      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .morpheus-header {
            padding: 16px 20px !important;
          }
          .morpheus-header h1 {
            font-size: 20px !important;
          }
          .morpheus-header p {
            font-size: 12px !important;
          }
          .district-content {
            padding: 20px !important;
          }
          .metrics-grid {
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)) !important;
            gap: 12px !important;
          }
          .metric-card {
            padding: 16px !important;
          }
          .metric-value {
            font-size: 28px !important;
          }
          .schools-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .school-card {
            padding: 20px !important;
          }
          .search-controls {
            padding: 16px !important;
          }
          .toolbar-wrapper {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
        }

        @media (max-width: 480px) {
          .metrics-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          .search-input {
            font-size: 16px !important; /* Prevent zoom on iOS */
          }
        }
      `}</style>

      {/* Header - iOS-style glassmorphism with Tappy colors and Logo */}
      <div className="morpheus-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 40px',
        backgroundColor: `rgba(252, 253, 254, 0.85)`,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: `0 1px 3px rgba(26, 62, 72, 0.08)`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img
            src="/images/TAPPY-logo-black.png"
            alt="Teach Tappy Logo"
            style={{
              height: '32px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: TappyBrandColors.neutral.darkSlate }}>
              District Overview
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: TappyBrandColors.primary.brandBlue }}>
              District Administrator • {user?.email}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Export Button - Official Tappy Brand with 2025 micro-interactions */}
          <button
            onClick={() => setShowExportModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`,
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
              e.currentTarget.style.boxShadow = `0 8px 16px rgba(41, 122, 146, 0.35)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = `0 2px 8px rgba(41, 122, 146, 0.25)`;
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
            }}
            aria-label="Export teaching quality report"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowExportModal(true);
              }
            }}
          >
            <span style={{ fontSize: '16px', transition: 'transform 200ms ease' }}>↓</span>
            Export
          </button>
          <button
            onClick={onLogout}
            style={{
              padding: '10px 20px',
              backgroundColor: TappyBrandColors.neutral.offWhite,
              color: TappyBrandColors.neutral.darkSlate,
              border: `1px solid ${TappyBrandColors.neutral.mintGray}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.offWhite;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* District-Wide Metrics */}
      <div className="district-content" style={{ padding: '40px', backgroundColor: TappyBrandColors.neutral.offWhite, minHeight: 'calc(100vh - 80px)' }}>
        {/* Toolbar: Refresh + Last Updated */}
        <div className="toolbar-wrapper" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: TappyBrandColors.neutral.darkSlate, margin: 0 }}>
            District-Wide Metrics
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            {lastUpdated && (
              <span style={{ fontSize: '13px', color: TappyBrandColors.primary.brandBlue }}>
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchDistrictOverview}
              disabled={loading}
              style={{
                padding: '8px 16px',
                backgroundColor: loading ? TappyBrandColors.neutral.mintGray : TappyBrandColors.primary.mediumTeal,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 200ms ease',
                boxShadow: loading ? 'none' : `0 2px 8px rgba(56, 136, 153, 0.25)`
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = TappyBrandColors.primary.mediumTeal;
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              🔄 {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Critical Alerts - Tappy Brand Colors */}
        {(expiringSchools > 0 || atCapacitySchools > 0) && (
          <div style={{ marginBottom: '24px' }}>
            {expiringSchools > 0 && (
              <div style={{
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                border: `2px solid ${TappyBrandColors.primary.confidenceFishGold}`,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                boxShadow: `0 4px 12px rgba(236, 201, 98, 0.15)`,
                borderLeft: `4px solid ${TappyBrandColors.accent.gold}`
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: 'white',
                  border: `2px solid ${TappyBrandColors.accent.gold}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: TappyBrandColors.accent.gold
                  }}>!</div>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '14px', color: TappyBrandColors.neutral.darkSlate, fontWeight: 600 }}>
                    {expiringSchools} school{expiringSchools > 1 ? 's' : ''} with licenses expiring in 60 days
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: TappyBrandColors.primary.brandBlue }}>
                    Review renewal options to avoid service interruption
                  </p>
                </div>
              </div>
            )}
            {atCapacitySchools > 0 && (
              <div style={{
                backgroundColor: TappyBrandColors.neutral.paleGray,
                border: `2px solid ${TappyBrandColors.accent.coral}`,
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                boxShadow: `0 4px 12px rgba(234, 109, 55, 0.15)`,
                borderLeft: `4px solid ${TappyBrandColors.accent.coral}`
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: 'white',
                  border: `2px solid ${TappyBrandColors.accent.coral}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: TappyBrandColors.accent.coral
                  }}>!</div>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '14px', color: TappyBrandColors.neutral.darkSlate, fontWeight: 600 }}>
                    {atCapacitySchools} school{atCapacitySchools > 1 ? 's' : ''} at or near capacity (90%+)
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: TappyBrandColors.primary.brandBlue }}>
                    Consider increasing seat licenses to accommodate growth
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="metrics-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '40px'
        }}>
          {/* Total Schools - Enhanced with 2025 micro-interactions */}
          <div className="metric-card" style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(79, 70, 229, 0.12)';
            e.currentTarget.style.borderColor = '#4F46E5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
          >
            {/* Gradient accent - Official Tappy Brand */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${TappyBrandColors.primary.teal}, ${TappyBrandColors.primary.darkTeal})`,
              opacity: 0.9
            }} />
            <div className="metric-value" style={{ fontSize: '36px', fontWeight: 700, color: TappyBrandColors.primary.teal, marginBottom: '8px', transition: 'all 200ms ease' }}>
              {districtMetrics?.total_schools || 0}
            </div>
            <div style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue, fontWeight: 500 }}>Total Schools</div>
          </div>

          {/* Total Students - Official Tappy Brand (skyBlue) */}
          <div className="metric-card" style={{
            backgroundColor: TappyBrandColors.neutral.nearWhite,
            padding: '24px',
            borderRadius: '16px',
            border: `1px solid ${TappyBrandColors.neutral.mintGray}`,
            boxShadow: '0 2px 8px rgba(26, 62, 72, 0.04)',
            transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0 12px 24px rgba(109, 180, 205, 0.15)`;
            e.currentTarget.style.borderColor = TappyBrandColors.primary.skyBlue;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(26, 62, 72, 0.04)';
            e.currentTarget.style.borderColor = TappyBrandColors.neutral.mintGray;
          }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${TappyBrandColors.primary.skyBlue}, ${TappyBrandColors.primary.aqua})`,
              opacity: 0.9
            }} />
            <div className="metric-value" style={{ fontSize: '36px', fontWeight: 700, color: TappyBrandColors.primary.skyBlue, marginBottom: '8px' }}>
              {districtMetrics?.total_students || 0}
            </div>
            <div style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue, fontWeight: 500 }}>Total Students</div>
            <div style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve, marginTop: '4px' }}>
              {((districtMetrics?.total_seats || 0) - (districtMetrics?.total_students || 0))} seats available
            </div>
          </div>

          {/* Avg Utilization - Official Tappy Brand (gold) */}
          <div className="metric-card" style={{
            backgroundColor: TappyBrandColors.neutral.nearWhite,
            padding: '24px',
            borderRadius: '16px',
            border: `1px solid ${TappyBrandColors.neutral.mintGray}`,
            boxShadow: '0 2px 8px rgba(26, 62, 72, 0.04)',
            transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0 12px 24px rgba(236, 201, 98, 0.15)`;
            e.currentTarget.style.borderColor = TappyBrandColors.accent.gold;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(26, 62, 72, 0.04)';
            e.currentTarget.style.borderColor = TappyBrandColors.neutral.mintGray;
          }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${TappyBrandColors.accent.gold}, ${TappyBrandColors.accent.brightGold})`,
              opacity: 0.9
            }} />
            <div className="metric-value" style={{ fontSize: '36px', fontWeight: 700, color: TappyBrandColors.accent.gold, marginBottom: '8px' }}>
              {districtMetrics?.avg_utilization_pct || 0}%
            </div>
            <div style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue, fontWeight: 500 }}>Seat Utilization</div>
          </div>

          {/* Total Teachers - Official Tappy Brand (mediumTeal) */}
          <div className="metric-card" style={{
            backgroundColor: TappyBrandColors.neutral.nearWhite,
            padding: '24px',
            borderRadius: '16px',
            border: `1px solid ${TappyBrandColors.neutral.mintGray}`,
            boxShadow: '0 2px 8px rgba(26, 62, 72, 0.04)',
            transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0 12px 24px rgba(56, 136, 153, 0.15)`;
            e.currentTarget.style.borderColor = TappyBrandColors.primary.mediumTeal;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(26, 62, 72, 0.04)';
            e.currentTarget.style.borderColor = TappyBrandColors.neutral.mintGray;
          }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${TappyBrandColors.primary.darkTeal}, ${TappyBrandColors.primary.mediumTeal})`,
              opacity: 0.9
            }} />
            <div className="metric-value" style={{ fontSize: '36px', fontWeight: 700, color: TappyBrandColors.primary.mediumTeal, marginBottom: '8px' }}>
              {districtMetrics?.total_teachers || 0}
            </div>
            <div style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue, fontWeight: 500 }}>Total Teachers</div>
            <div style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve, marginTop: '4px' }}>
              {districtMetrics?.active_teachers || 0} active
            </div>
          </div>

          {/* Total Learning Hours - Official Tappy Brand (brightTeal) */}
          <div className="metric-card" style={{
            backgroundColor: TappyBrandColors.neutral.nearWhite,
            padding: '24px',
            borderRadius: '16px',
            border: `1px solid ${TappyBrandColors.neutral.mintGray}`,
            boxShadow: '0 2px 8px rgba(26, 62, 72, 0.04)',
            transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0 12px 24px rgba(66, 144, 158, 0.15)`;
            e.currentTarget.style.borderColor = TappyBrandColors.primary.brightTeal;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(26, 62, 72, 0.04)';
            e.currentTarget.style.borderColor = TappyBrandColors.neutral.mintGray;
          }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${TappyBrandColors.primary.brightTeal}, ${TappyBrandColors.primary.lightAqua})`,
              opacity: 0.9
            }} />
            <div className="metric-value" style={{ fontSize: '36px', fontWeight: 700, color: TappyBrandColors.primary.brightTeal, marginBottom: '8px' }}>
              {districtMetrics?.total_learning_hours || 0}
            </div>
            <div style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue, fontWeight: 500 }}>Learning Hours</div>
          </div>
        </div>

        {/* Empty State Help Message - Official Tappy Brand */}
        {districtMetrics && districtMetrics.total_students === 0 && (
          <div style={{
            backgroundColor: TappyBrandColors.neutral.paleGray,
            padding: '18px',
            borderRadius: '12px',
            border: `2px solid ${TappyBrandColors.primary.brightTeal}`,
            marginTop: '24px',
            marginBottom: '24px',
            boxShadow: `0 4px 12px rgba(66, 144, 158, 0.15)`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: 'white',
                border: `2px solid ${TappyBrandColors.primary.brightTeal}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: TappyBrandColors.primary.brightTeal
                }}>i</div>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: '14px', color: TappyBrandColors.neutral.darkSlate, fontWeight: 600, lineHeight: 1.5 }}>
                  Waiting for first student login
                </p>
                <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: TappyBrandColors.primary.brandBlue, lineHeight: 1.5 }}>
                  Metrics will populate after students log in via Clever SSO.
                  Schoology roster ({districtMetrics.lms_students_synced || 0} students synced, {districtMetrics.lms_courses_synced || 0} courses) is ready.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* District-Wide Financial ROI - Optimized for Renewals */}
        {districtMetrics && reteachROI && districtMetrics.total_students > 0 && districtMetrics.total_learning_hours > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{
                  height: '3px',
                  width: '40px',
                  background: `linear-gradient(90deg, ${TappyBrandColors.primary.teal}, ${TappyBrandColors.primary.skyBlue})`,
                  borderRadius: '2px'
                }}></div>
                <h2 style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: TappyBrandColors.neutral.darkSlate,
                  margin: 0,
                  letterSpacing: '-0.3px'
                }}>
                  District-Wide Investment & ROI
                </h2>
              </div>
              <p style={{
                fontSize: '14px',
                color: TappyBrandColors.primary.brandBlue,
                margin: '8px 0 0 52px',
                lineHeight: '1.6'
              }}>
                Aggregate financial impact across {districtMetrics.total_schools || 0} schools • Last 30 Days
              </p>
            </div>

            {/* ROI Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '28px' }}>
              {/* Total Annual Investment */}
              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                  Total Annual Investment
                </div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>
                  ${(districtMetrics.total_annual_cost || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                  {districtMetrics.total_students > 0
                    ? `$${Math.round((districtMetrics.total_annual_cost || 0) / districtMetrics.total_students).toLocaleString()} per active student`
                    : 'Cost per student unavailable'}
                </div>
              </div>

              {/* Intervention Cost Avoided */}
              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                  Intervention Cost Avoided
                </div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981' }}>
                  ${Math.round((districtMetrics.total_learning_hours || 0) * 70).toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                  At $70/hour for intervention specialists
                </div>
              </div>

              {/* Teacher Capacity Saved */}
              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                  Teacher Capacity Saved
                </div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981' }}>
                  {reteachROI.district_summary?.total_time_saved_hours?.toFixed(1) || '0.0'}h
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                  ${Math.round((reteachROI.district_summary?.total_time_saved_hours || 0) * 45).toLocaleString()} value (at $45/hour)
                </div>
              </div>
            </div>

            {/* Impact Summary - Renewal Messaging */}
            <div style={{
              marginTop: '24px',
              padding: '20px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              border: '1px solid #22c55e'
            }}>
              <div style={{ fontSize: '14px', color: '#15803d', lineHeight: '1.8' }}>
                <strong>District-Wide Impact for Board Presentation:</strong> Your district delivered <strong>{Math.round(districtMetrics.total_learning_hours || 0).toLocaleString()} hours</strong> of personalized learning across <strong>{districtMetrics.total_schools || 0} schools</strong> at <strong>${Math.round((districtMetrics.total_annual_cost || 0) / (districtMetrics.total_students || 1)).toLocaleString()} per active student</strong>—replacing expensive intervention programs that would cost <strong>${Math.round((districtMetrics.total_learning_hours || 0) * 70).toLocaleString()}</strong> at specialist rates.
                {reteachROI.district_summary?.total_time_saved_hours > 0 && (
                  <span> Tappy also saved <strong>{reteachROI.district_summary.total_time_saved_hours.toFixed(1)}h of teacher time</strong> across the district by catching misconceptions early, keeping your best teachers focused on instruction instead of remediation.</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Energy-Based Learning Loading State */}
        {energyLoading && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '16px', color: '#6b7280', marginBottom: '12px' }}>
              Loading energy-based learning insights...
            </div>
            <div style={{ fontSize: '14px', color: '#9ca3af' }}>
              Analyzing teaching quality, ROI metrics, and universal patterns across your district
            </div>
          </div>
        )}

        {/* Teaching Quality Across District - Energy-Based Learning */}
        {!energyLoading && teachingQuality && teachingQuality.schools && teachingQuality.schools.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{
                  height: '3px',
                  width: '40px',
                  background: `linear-gradient(90deg, ${TappyBrandColors.primary.teal}, ${TappyBrandColors.primary.skyBlue})`,
                  borderRadius: '2px'
                }}></div>
                <h2 style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: TappyBrandColors.neutral.darkSlate,
                  margin: 0,
                  letterSpacing: '-0.3px'
                }}>
                  Teaching Quality Across District
                </h2>
              </div>
              <p style={{
                fontSize: '14px',
                color: TappyBrandColors.primary.brandBlue,
                margin: '8px 0 0 52px',
                lineHeight: '1.6'
              }}>
                Energy-Based Learning: Lower energy scores indicate more natural, effective teaching
              </p>
            </div>

            {/* Bar Chart */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: TappyBrandColors.primary.darkTeal,
                marginBottom: '20px',
                letterSpacing: '0.3px',
                textTransform: 'uppercase'
              }}>
                Average Teaching Energy by School · Last 30 Days
              </div>
              {teachingQuality.schools.map((school, index) => {
                const barWidth = school.avg_energy * 100;
                let barColor;
                if (school.status === 'excellent') {
                  barColor = TappyBrandColors.primary.skyBlue;
                } else if (school.status === 'good') {
                  barColor = TappyBrandColors.primary.teal;
                } else if (school.status === 'fair') {
                  barColor = TappyBrandColors.accent.gold;
                } else {
                  barColor = TappyBrandColors.accent.coral;
                }

                return (
                  <div key={school.school_id} style={{
                    marginBottom: '16px',
                    padding: '16px',
                    backgroundColor: index % 2 === 0 ? TappyBrandColors.neutral.offWhite : 'white',
                    borderRadius: '10px',
                    border: `1px solid ${TappyBrandColors.neutral.lightGray}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: TappyBrandColors.neutral.darkSlate
                      }}>
                        {school.school_name}
                      </span>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: barColor,
                        padding: '4px 10px',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        border: `1px solid ${barColor}`
                      }}>
                        {school.avg_energy} · {school.quality_label}
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: TappyBrandColors.neutral.paleGray,
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${barWidth}%`,
                        height: '100%',
                        backgroundColor: barColor,
                        transition: 'width 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        borderRadius: '4px'
                      }}></div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: TappyBrandColors.primary.brandBlue,
                      marginTop: '8px'
                    }}>
                      {school.num_actions} teaching actions analyzed · {school.effective_actions_pct}% highly effective
                    </div>
                  </div>
                );
              })}
            </div>

            {/* District Insight */}
            <div style={{
              backgroundColor: TappyBrandColors.neutral.offWhite,
              border: `2px solid ${TappyBrandColors.primary.brightTeal}`,
              borderRadius: '12px',
              padding: '20px',
              marginTop: '28px',
              borderLeft: `4px solid ${TappyBrandColors.primary.brightTeal}`
            }}>
              <div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: TappyBrandColors.primary.darkTeal,
                  marginBottom: '12px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  District Insight
                </div>
                <div style={{
                  fontSize: '14px',
                  color: TappyBrandColors.neutral.darkSlate,
                  lineHeight: '1.7'
                }}>
                  {teachingQuality.schools[0] && teachingQuality.schools[teachingQuality.schools.length - 1] && (
                    <>
                      <strong style={{ color: TappyBrandColors.primary.darkTeal }}>{teachingQuality.schools[0].school_name}</strong> shows excellent teaching energy ({teachingQuality.schools[0].avg_energy}).
                      {teachingQuality.schools.length > 1 && teachingQuality.schools[teachingQuality.schools.length - 1].status !== 'excellent' && (
                        <> Consider sharing best practices with <strong style={{ color: TappyBrandColors.primary.darkTeal }}>{teachingQuality.schools[teachingQuality.schools.length - 1].school_name}</strong> ({teachingQuality.schools[teachingQuality.schools.length - 1].avg_energy} energy).</>
                      )}
                    </>
                  )}
                  <div style={{ marginTop: '10px', fontSize: '13px' }}>
                    <strong style={{ color: TappyBrandColors.primary.teal }}>District Average:</strong> {teachingQuality.district_summary.avg_energy} energy across {teachingQuality.district_summary.num_schools_with_data} school{teachingQuality.district_summary.num_schools_with_data !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Re-teach Prevention ROI - Energy-Based Learning */}
        {!energyLoading && reteachROI && reteachROI.schools && reteachROI.schools.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{ height: '4px', width: '32px', backgroundColor: '#10b981', borderRadius: '2px' }}></div>
                <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', margin: 0 }}>
                  💰 Re-teach Prevention ROI by School
                </h2>
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '8px 0 0 44px' }}>
                Time saved by detecting and addressing misconceptions early (Last Quarter)
              </p>
            </div>

            {/* ROI Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>School</th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>Time Saved</th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>ROI</th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>Adoption</th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reteachROI.schools.map((school, index) => {
                    let statusColor, statusLabel;
                    if (school.status === 'excellent') {
                      statusColor = TappyBrandColors.primary.skyBlue;
                      statusLabel = 'Excellent';
                    } else if (school.status === 'good') {
                      statusColor = TappyBrandColors.primary.teal;
                      statusLabel = 'Good';
                    } else if (school.status === 'fair') {
                      statusColor = TappyBrandColors.accent.gold;
                      statusLabel = 'Fair';
                    } else {
                      statusColor = TappyBrandColors.accent.coral;
                      statusLabel = 'Low';
                    }

                    return (
                      <tr key={school.school_id} style={{
                        borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`,
                        backgroundColor: index % 2 === 0 ? TappyBrandColors.neutral.offWhite : 'white'
                      }}>
                        <td style={{
                          padding: '16px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: TappyBrandColors.neutral.darkSlate
                        }}>
                          {school.school_name}
                        </td>
                        <td style={{
                          padding: '16px',
                          fontSize: '14px',
                          color: TappyBrandColors.neutral.darkSlate,
                          textAlign: 'center'
                        }}>
                          <div style={{ fontWeight: 600 }}>{school.time_saved_hours}h</div>
                          <div style={{
                            fontSize: '12px',
                            color: TappyBrandColors.primary.brandBlue
                          }}>{school.misconceptions_detected} caught</div>
                        </td>
                        <td style={{
                          padding: '16px',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: TappyBrandColors.primary.teal,
                          textAlign: 'center'
                        }}>
                          {school.roi_multiplier}x
                        </td>
                        <td style={{
                          padding: '16px',
                          fontSize: '14px',
                          color: TappyBrandColors.neutral.darkSlate,
                          textAlign: 'center'
                        }}>
                          <div style={{ fontWeight: 600 }}>{school.teacher_adoption_pct}%</div>
                          <div style={{
                            fontSize: '12px',
                            color: TappyBrandColors.primary.brandBlue
                          }}>{school.teachers_using}/{school.total_teachers}</div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '6px 14px',
                            backgroundColor: 'white',
                            color: statusColor,
                            border: `1.5px solid ${statusColor}`,
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            {statusLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* District Summary - iOS-style clean design */}
            <div style={{
              backgroundColor: TappyBrandColors.neutral.nearWhite,
              border: `2px solid ${TappyBrandColors.primary.skyBlue}`,
              borderRadius: '12px',
              padding: '24px',
              marginTop: '24px',
              boxShadow: `0 4px 12px rgba(109, 180, 205, 0.12)`
            }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: TappyBrandColors.neutral.darkSlate, marginBottom: '16px', letterSpacing: '0.5px' }}>
                DISTRICT IMPACT · Last Quarter
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${TappyBrandColors.neutral.mintGray}` }}>
                  <div style={{ fontSize: '11px', color: TappyBrandColors.primary.brandBlue, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 500 }}>Total Time Saved</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: TappyBrandColors.primary.teal, marginBottom: '4px' }}>
                    {reteachROI.district_summary.total_time_saved_hours}h
                  </div>
                  <div style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve }}>
                    {reteachROI.district_summary.total_misconceptions_detected} misconceptions caught
                  </div>
                </div>
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${TappyBrandColors.neutral.mintGray}` }}>
                  <div style={{ fontSize: '11px', color: TappyBrandColors.primary.brandBlue, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 500 }}>Hiring Equivalent</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: TappyBrandColors.primary.teal, marginBottom: '4px' }}>
                    {reteachROI.district_summary.hiring_equivalent} FTE
                  </div>
                  <div style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve }}>
                    Full-time teachers at zero cost
                  </div>
                </div>
              </div>
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                color: TappyBrandColors.neutral.darkSlate,
                lineHeight: '1.6',
                borderLeft: `3px solid ${TappyBrandColors.primary.skyBlue}`
              }}>
                <strong style={{ color: TappyBrandColors.primary.teal }}>Board-Ready Metric:</strong> Your district saved {reteachROI.district_summary.total_time_saved_hours} hours this quarter by proactively detecting misconceptions. This equals {reteachROI.district_summary.hiring_equivalent} full-time teacher equivalents at zero additional cost.
              </div>
            </div>
          </div>
        )}

        {/* Universal Teaching Patterns - iOS-style clean design */}
        {!energyLoading && universalPatterns && universalPatterns.patterns && universalPatterns.patterns.length > 0 && (
          <div style={{
            backgroundColor: TappyBrandColors.neutral.nearWhite,
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
            boxShadow: `0 2px 8px rgba(26, 62, 72, 0.06)`,
            border: `1px solid ${TappyBrandColors.neutral.mintGray}`
          }}>
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ height: '3px', width: '40px', background: `linear-gradient(90deg, ${TappyBrandColors.primary.teal}, ${TappyBrandColors.primary.skyBlue})`, borderRadius: '2px' }}></div>
                <h2 style={{ fontSize: '22px', fontWeight: 600, color: TappyBrandColors.neutral.darkSlate, margin: 0, letterSpacing: '-0.3px' }}>
                  Universal Teaching Patterns
                </h2>
              </div>
              <p style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue, margin: '0 0 0 52px', lineHeight: 1.5 }}>
                What works across your entire district · {universalPatterns.district_summary.total_students.toLocaleString()} students · {universalPatterns.district_summary.total_actions.toLocaleString()} teaching interactions
              </p>
            </div>

            {/* District Summary Banner */}
            <div style={{
              backgroundColor: TappyBrandColors.neutral.nearWhite,
              border: `2px solid ${TappyBrandColors.primary.skyBlue}`,
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '28px',
              boxShadow: `0 2px 8px rgba(109, 180, 205, 0.1)`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px' }}>
                <div>
                  <div style={{
                    fontSize: '12px',
                    color: TappyBrandColors.primary.darkTeal,
                    fontWeight: 600,
                    marginBottom: '6px',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>
                    District Average Effectiveness
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: TappyBrandColors.primary.teal }}>
                    {(universalPatterns.district_summary.avg_effectiveness * 100).toFixed(1)}%
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: '300px' }}>
                  <div style={{ fontSize: '13px', color: TappyBrandColors.neutral.darkSlate, lineHeight: '1.7' }}>
                    <strong style={{ color: TappyBrandColors.primary.darkTeal }}>Volume Regularization:</strong> These patterns emerge from analyzing {universalPatterns.district_summary.total_actions.toLocaleString()} teaching interactions across your entire district. High-coverage patterns indicate approaches that work for most students.
                  </div>
                </div>
              </div>
            </div>

            {/* Teaching Patterns Grid */}
            <div style={{ display: 'grid', gap: '16px' }}>
              {universalPatterns.patterns.map((pattern, idx) => {
                // Color scheme based on effectiveness category - Tappy Brand Colors
                const categoryColors = {
                  'Highly Effective': {
                    bg: TappyBrandColors.neutral.nearWhite,
                    border: TappyBrandColors.primary.skyBlue,
                    text: TappyBrandColors.primary.darkTeal,
                    badge: TappyBrandColors.primary.skyBlue
                  },
                  'Effective': {
                    bg: TappyBrandColors.neutral.offWhite,
                    border: TappyBrandColors.primary.teal,
                    text: TappyBrandColors.primary.darkTeal,
                    badge: TappyBrandColors.primary.teal
                  },
                  'Moderately Effective': {
                    bg: TappyBrandColors.neutral.offWhite,
                    border: TappyBrandColors.accent.gold,
                    text: TappyBrandColors.neutral.darkSlate,
                    badge: TappyBrandColors.accent.gold
                  },
                  'Low Effectiveness': {
                    bg: TappyBrandColors.neutral.paleGray,
                    border: TappyBrandColors.accent.coral,
                    text: TappyBrandColors.neutral.darkSlate,
                    badge: TappyBrandColors.accent.coral
                  }
                };
                const colors = categoryColors[pattern.effectiveness_label] || categoryColors['Moderately Effective'];

                return (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: colors.bg,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      padding: '24px',
                      transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      cursor: 'default',
                      boxShadow: '0 1px 3px rgba(26, 62, 72, 0.08)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(26, 62, 72, 0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(26, 62, 72, 0.08)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                          <span style={{
                            fontSize: '17px',
                            fontWeight: 600,
                            color: colors.text,
                            letterSpacing: '-0.2px'
                          }}>
                            {pattern.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: colors.text, opacity: 0.75 }}>
                          Used in {pattern.total_actions.toLocaleString()} teaching interactions
                        </div>
                      </div>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '6px 12px',
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        color: colors.badge,
                        border: `1.5px solid ${colors.border}`,
                        whiteSpace: 'nowrap'
                      }}>
                        {pattern.effectiveness_label}
                      </div>
                    </div>

                    {/* Metrics Row */}
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <div style={{ fontSize: '11px', color: colors.text, opacity: 0.8, marginBottom: '2px' }}>
                          Effectiveness Score
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: colors.text }}>
                          {(pattern.avg_effectiveness * 100).toFixed(1)}%
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <div style={{ fontSize: '11px', color: colors.text, opacity: 0.8, marginBottom: '2px' }}>
                          Success Rate
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: colors.text }}>
                          {pattern.success_rate_pct}%
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <div style={{ fontSize: '11px', color: colors.text, opacity: 0.8, marginBottom: '2px' }}>
                          Student Coverage
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: colors.text }}>
                          {pattern.student_coverage_pct}%
                        </div>
                        <div style={{ fontSize: '10px', color: colors.text, opacity: 0.7 }}>
                          {pattern.students_benefiting.toLocaleString()} students
                        </div>
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '14px',
                      border: `1.5px solid ${colors.border}`,
                      borderLeft: `4px solid ${colors.badge}`
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: colors.text,
                        marginBottom: '6px',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                      }}>
                        Recommended Action
                      </div>
                      <div style={{ fontSize: '13px', color: colors.text, lineHeight: '1.6', opacity: 0.9 }}>
                        {pattern.recommendation}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer Insight */}
            <div style={{
              marginTop: '28px',
              padding: '20px',
              backgroundColor: TappyBrandColors.neutral.offWhite,
              borderRadius: '12px',
              border: `1px solid ${TappyBrandColors.neutral.mintGray}`,
              borderLeft: `4px solid ${TappyBrandColors.primary.brightTeal}`
            }}>
              <div style={{ fontSize: '13px', color: TappyBrandColors.neutral.darkSlate, lineHeight: '1.7' }}>
                <strong style={{ color: TappyBrandColors.primary.darkTeal }}>How to Use This Data:</strong> High-coverage patterns (70%+ students) indicate district-wide trends. Prioritize training on highly effective patterns with low adoption. Compare patterns across schools to identify where specific approaches are succeeding or struggling.
              </div>
            </div>
          </div>
        )}

        {/* Schools List */}
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', marginBottom: '24px' }}>
          Your Schools
        </h2>

        {/* Search and Filter Controls */}
        <div className="search-controls" style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '24px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'center'
        }}>
          {/* Search Input */}
          <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search schools by name or district ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                border: `1px solid ${TappyBrandColors.neutral.lightGray}`,
                borderRadius: '8px',
                outline: 'none',
                transition: 'all 0.2s',
                backgroundColor: 'white'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = TappyBrandColors.primary.teal;
                e.target.style.boxShadow = `0 0 0 3px rgba(41, 122, 146, 0.1)`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = TappyBrandColors.neutral.lightGray;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Filter Dropdown */}
          <div style={{ flex: '0 1 auto' }}>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '10px 14px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                minWidth: '160px'
              }}
            >
              <option value="all">All Schools</option>
              <option value="active">Active Only</option>
              <option value="expiring">Expiring Soon</option>
              <option value="at-risk">At Capacity</option>
            </select>
          </div>

          {/* Sort Dropdown */}
          <div style={{ flex: '0 1 auto' }}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '10px 14px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                minWidth: '160px'
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="utilization">Sort by Utilization</option>
              <option value="students">Sort by Students</option>
              <option value="expiration">Sort by Expiration</option>
            </select>
          </div>

          {/* Results Count */}
          {(searchTerm || filterStatus !== 'all') && (
            <div style={{ flex: '1 1 100%', fontSize: '13px', color: '#6b7280' }}>
              Showing {filteredSchools.length} of {schools.length} schools
            </div>
          )}
        </div>

        <div className="schools-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px'
        }}>
          {filteredSchools.map(school => (
            <div
              key={school.id}
              onClick={() => handleSchoolClick(school.uuid)}
              className="school-card"
              style={{
                backgroundColor: 'white',
                padding: '28px',
                borderRadius: '16px',
                border: `2px solid ${TappyBrandColors.neutral.lightGray}`,
                cursor: 'pointer',
                transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: '0 1px 3px rgba(26, 62, 72, 0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = TappyBrandColors.primary.teal;
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 24px rgba(41, 122, 146, 0.15)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = TappyBrandColors.neutral.lightGray;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(26, 62, 72, 0.08)';
              }}
            >
              <div style={{ marginBottom: '24px', borderBottom: `2px solid ${TappyBrandColors.neutral.paleGray}`, paddingBottom: '16px' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: TappyBrandColors.primary.darkTeal,
                  margin: 0,
                  letterSpacing: '-0.3px'
                }}>
                  {school.name}
                </h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                <div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: TappyBrandColors.primary.teal
                  }}>
                    {school.metrics?.seats_used || 0}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: TappyBrandColors.primary.brandBlue,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Students</div>
                </div>

                <div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: TappyBrandColors.primary.skyBlue
                  }}>
                    {school.metrics?.utilization_pct || 0}%
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: TappyBrandColors.primary.brandBlue,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Utilization</div>
                </div>

                <div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: TappyBrandColors.accent.gold
                  }}>
                    {school.teachers?.total || 0}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: TappyBrandColors.primary.brandBlue,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Teachers</div>
                </div>

                <div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: TappyBrandColors.primary.brightTeal
                  }}>
                    {school.learning_time?.total_hours || 0}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: TappyBrandColors.primary.brandBlue,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Hours</div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: school.learning_independence?.avg_score >= 70 ? '#10b981' :
                           school.learning_independence?.avg_score >= 40 ? '#f59e0b' :
                           '#ef4444'
                  }}>
                    {school.learning_independence?.avg_score || 0}/100
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: TappyBrandColors.primary.brandBlue,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Independence Avg</div>
                </div>
              </div>

              {/* Energy-Based Learning Badge */}
              {school.energy && school.energy.avg_energy !== null && (
                <div style={{
                  marginTop: '20px',
                  padding: '18px',
                  backgroundColor: TappyBrandColors.neutral.paleGray,
                  borderRadius: '12px',
                  border: `1.5px solid ${TappyBrandColors.neutral.mintGray}`
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: TappyBrandColors.primary.darkTeal,
                    marginBottom: '14px',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>
                    Teaching Quality
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '12px', color: TappyBrandColors.neutral.darkSlate }}>Avg Energy Score:</span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      padding: '6px 14px',
                      borderRadius: '12px',
                      backgroundColor: school.energy.quality_label === 'Excellent' ? TappyBrandColors.neutral.nearWhite :
                                      school.energy.quality_label === 'Good' ? TappyBrandColors.neutral.offWhite :
                                      school.energy.quality_label === 'Fair' ? TappyBrandColors.neutral.offWhite :
                                      TappyBrandColors.neutral.paleGray,
                      border: school.energy.quality_label === 'Excellent' ? `1.5px solid ${TappyBrandColors.primary.skyBlue}` :
                              school.energy.quality_label === 'Good' ? `1.5px solid ${TappyBrandColors.primary.teal}` :
                              school.energy.quality_label === 'Fair' ? `1.5px solid ${TappyBrandColors.accent.gold}` :
                              `1.5px solid ${TappyBrandColors.accent.coral}`,
                      color: school.energy.quality_label === 'Excellent' ? TappyBrandColors.primary.skyBlue :
                             school.energy.quality_label === 'Good' ? TappyBrandColors.primary.teal :
                             school.energy.quality_label === 'Fair' ? TappyBrandColors.accent.gold :
                             TappyBrandColors.accent.coral
                    }}>
                      {school.energy.avg_energy} - {school.energy.quality_label}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: TappyBrandColors.primary.brandBlue }}>
                    {school.energy.num_actions} actions analyzed · Lower energy = more natural teaching
                  </div>
                </div>
              )}

              <div style={{
                paddingTop: '20px',
                borderTop: `1px solid ${TappyBrandColors.neutral.lightGray}`,
                fontSize: '15px',
                color: TappyBrandColors.primary.teal,
                fontWeight: 600,
                textAlign: 'center'
              }}>
                View School Dashboard →
              </div>
            </div>
          ))}
        </div>

        {schools.length === 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '80px 60px',
            borderRadius: '16px',
            border: `2px solid ${TappyBrandColors.neutral.mintGray}`,
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 24px',
              borderRadius: '20px',
              backgroundColor: TappyBrandColors.neutral.paleGray,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px solid ${TappyBrandColors.neutral.mintGray}`
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: TappyBrandColors.primary.teal,
                opacity: 0.2
              }} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: TappyBrandColors.primary.darkTeal,
              marginBottom: '8px',
              letterSpacing: '-0.3px'
            }}>
              No Schools Found
            </h3>
            <p style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue }}>
              No schools are associated with your district account.
            </p>
          </div>
        )}

        {/* Teach Tappy Footer - Consistent with TutorInterface */}
        <div style={{
          marginTop: '60px',
          backgroundColor: 'rgba(245, 249, 250, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(213, 222, 221, 0.5)',
          padding: '1rem',
          textAlign: 'center',
          fontSize: '0.65rem',
          color: '#76909f'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.3rem',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <img
              src="/images/TAPPY-logo-black.png"
              alt="Tappy Logo"
              style={{
                height: '18px',
                width: 'auto',
                objectFit: 'contain',
                marginBottom: '0.2rem'
              }}
            />
            <p style={{ margin: 0, fontSize: '0.65rem', lineHeight: '1.4' }}>
              &copy; {new Date().getFullYear()} Teach Tappy | Powered by <span style={{ fontWeight: 500 }}>Morpheus RL</span>
            </p>
            <p style={{ margin: 0, fontSize: '0.6rem', lineHeight: '1.4' }}>
              By using our services, you agree to Tappy's{' '}
              <a
                href="/terms-of-service.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#76909f',
                  textDecoration: 'underline'
                }}
              >
                Terms of Use
              </a>
              {' '}and{' '}
              <a
                href="/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#76909f',
                  textDecoration: 'underline'
                }}
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Export Modal - Modern 2025 Design */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        exportType="district"
      />
    </div>
  );
}

export default DistrictAdminDashboard;
